import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  AppBar, Toolbar, Typography, Button, Container, Box, Grid, Card, CardContent,
  Chip, Divider, Stack
} from "@mui/material";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Landing() {
  const nav = useMemo(() => ([
    { label: "Inicio", id: "top" },
    { label: "UPV · IGIC", id: "upv-igic" },
    { label: "Grupo", id: "group" },
    { label: "Proyectos", id: "projects" },
    { label: "Avances", id: "advances" },
    { label: "Publicaciones", id: "publications" },
    { label: "Contacto", id: "contact" },
  ]), []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#0b0f1a", color: "rgba(255,255,255,0.92)" }} id="top">
      {/* NAV */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: "rgba(11,15,26,0.85)", backdropFilter: "blur(10px)" }}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.3, mr: 2 }}>
            Comunicaciones y Redes · IGIC
          </Typography>

          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.5, flex: 1 }}>
            {nav.map((n) => (
              <Button key={n.id} color="inherit" onClick={() => scrollToId(n.id)} sx={{ textTransform: "none", opacity: 0.9 }}>
                {n.label}
              </Button>
            ))}
          </Box>

          <Button
            component={RouterLink}
            to="/login"
            variant="contained"
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Acceder
          </Button>
        </Toolbar>
      </AppBar>

      {/* HERO */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              Plataforma de proyectos y observatorios IoT
            </Typography>
            <Typography sx={{ mt: 2, opacity: 0.85, maxWidth: 720 }}>
              Difusión científica (proyectos, publicaciones y avances) + acceso seguro al panel de visualización de datos
              para prototipos multiparamétricos (mapa 2D/3D, tablas, exportación, etc.).
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>
              <Button onClick={() => scrollToId("projects")} variant="outlined" color="inherit" sx={{ textTransform: "none", borderRadius: 2 }}>
                Ver proyectos
              </Button>
              <Button component={RouterLink} to="/login" variant="contained" sx={{ textTransform: "none", borderRadius: 2 }}>
                Acceder al panel
              </Button>
              <Button component={RouterLink} to="/map" variant="text" color="inherit" sx={{ textTransform: "none", opacity: 0.9 }}>
                Ver mapa (requiere login)
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 3, flexWrap: "wrap" }}>
              <Chip label="Edge AI" variant="outlined" sx={{ color: "white", borderColor: "rgba(255,255,255,0.25)" }} />
              <Chip label="Digital Twins" variant="outlined" sx={{ color: "white", borderColor: "rgba(255,255,255,0.25)" }} />
              <Chip label="Marine & Coastal Monitoring" variant="outlined" sx={{ color: "white", borderColor: "rgba(255,255,255,0.25)" }} />
              <Chip label="LoRa / WiFi / IoT" variant="outlined" sx={{ color: "white", borderColor: "rgba(255,255,255,0.25)" }} />
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 4, border: "1px solid rgba(255,255,255,0.10)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Qué encontrarás aquí</Typography>
                <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.12)" }} />
                <Stack spacing={1.2} sx={{ opacity: 0.9 }}>
                  <Typography>• Landing pública: proyectos, objetivos, actividades, avances y contacto.</Typography>
                  <Typography>• Panel privado: tablas, mapa 2D/3D, exportación CSV, nodos en tiempo real.</Typography>
                  <Typography>• Secciones automatizadas: News/Advances, Publications, Calendar, datasets.</Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* UPV + IGIC */}
      <Container maxWidth="lg" sx={{ pb: 8 }} id="upv-igic">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>UPV · IGIC</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 4, border: "1px solid rgba(255,255,255,0.10)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Universitat Politècnica de València</Typography>
                <Typography sx={{ mt: 1, opacity: 0.85 }}>
                  UPV combina formación e investigación. Dispone de una estructura amplia (departamentos, centros e institutos)
                  y producción de resultados/transferencia.
                </Typography>
                <Typography sx={{ mt: 2, opacity: 0.9 }}>
                  Indicadores (según memoria del proyecto): 41 departamentos, 40 centros e institutos, 4.055 personas en estructuras
                  de investigación, 222 patentes, 56 másteres y 28 doctorados.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 4, border: "1px solid rgba(255,255,255,0.10)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>IGIC (Integrated Management Coastal Research Institute)</Typography>
                <Typography sx={{ mt: 1, opacity: 0.85 }}>
                  Objetivo general: promover y realizar investigación excelente en gestión integrada de zonas costeras.
                </Typography>
                <Typography sx={{ mt: 2, opacity: 0.9 }}>
                  Áreas: (1) conservación y estudio ambiental de recursos biológicos costeros; (2) herramientas tecnológicas aplicadas
                  a entornos marinos y costeros; (3) conocimiento, planificación y gestión de zona costera.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Grupo */}
      <Container maxWidth="lg" sx={{ pb: 8 }} id="group">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>Grupo de Comunicaciones y Redes</Typography>
        <Card sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 4, border: "1px solid rgba(255,255,255,0.10)" }}>
          <CardContent>
            <Typography sx={{ opacity: 0.9 }}>
              Enfoque: diseño de redes de sensores de bajo coste, comunicaciones (incluyendo escenarios complejos como entornos
              subacuáticos), edge computing e IA ligera para monitorización ambiental.
            </Typography>
            <Typography sx={{ mt: 2, opacity: 0.85 }}>
              Experiencia en comunicaciones subacuáticas, redes grupales y optimización energética,
              además de sensores multiparamétricos e integración de edge/ML.
            </Typography>
          </CardContent>
        </Card>
      </Container>

      {/* Proyectos / Avances / Publicaciones */}
      <Container maxWidth="lg" sx={{ pb: 8 }} id="projects">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>Proyectos</Typography>
        <Typography sx={{ opacity: 0.85, mb: 2 }}>
          (Automatizado desde Firestore: listado, estado, objetivos, enlaces, nodos y dashboards asociados).
        </Typography>

        <Grid container spacing={2}>
          {["GRIDSEA", "ThinkInAzul", "Agricultura 6.0"].map((p) => (
            <Grid item xs={12} md={4} key={p}>
              <Card sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 4, border: "1px solid rgba(255,255,255,0.10)", height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{p}</Typography>
                  <Typography sx={{ mt: 1, opacity: 0.85 }}>
                    Resumen + objetivos + actividades + enlaces (rellenado desde BD).
                  </Typography>
                  <Button onClick={() => scrollToId("advances")} sx={{ mt: 2, textTransform: "none" }} color="inherit">
                    Ver avances →
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="lg" sx={{ pb: 8 }} id="advances">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>Avances y noticias</Typography>
        <Typography sx={{ opacity: 0.85 }}>
          La memoria propone que la web publique resultados, noticias y “latest advances”, además de comunicación con investigadores.
        </Typography>
      </Container>

      <Container maxWidth="lg" sx={{ pb: 8 }} id="publications">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>Publicaciones</Typography>
        <Typography sx={{ opacity: 0.85 }}>
          Listado automatizado (OA cuando sea posible, repositorios, enlaces a datasets/software y documentación).
        </Typography>
      </Container>

      {/* Contacto */}
      <Container maxWidth="lg" sx={{ pb: 10 }} id="contact">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>Contacto</Typography>
        <Card sx={{ bgcolor: "rgba(255,255,255,0.06)", borderRadius: 4, border: "1px solid rgba(255,255,255,0.10)" }}>
          <CardContent>
            <Typography sx={{ opacity: 0.9 }}>
              Formulario + emails institucionales + enlaces (ResearchGate/LinkedIn) + colaboración con stakeholders.
            </Typography>
          </CardContent>
        </Card>
      </Container>

      <Box sx={{ py: 3, borderTop: "1px solid rgba(255,255,255,0.08)", textAlign: "center", opacity: 0.7 }}>
        © UPV · IGIC · Comunicaciones y Redes
      </Box>
    </Box>
  );
}
